import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Switch } from '../ui/switch';
import { Coins, Search, TrendingDown, IndianRupee, Wallet, Loader2, RefreshCw, Building2, User as UserIcon, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

const PERIODS = [
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
  { value: '180', label: 'Last 180 days' },
  { value: '365', label: 'Last 1 year' },
  { value: 'custom', label: 'Custom…' },
];

export const CreditsOverviewTab = ({ api, isAdmin }) => {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30');
  const [customDays, setCustomDays] = useState('30');
  const [typeFilter, setTypeFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [detail, setDetail] = useState(null);
  const [detailTxns, setDetailTxns] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [renewing, setRenewing] = useState(false);
  const [revise, setRevise] = useState(null); // { monthly_allocation, balance, auto_renew, reason } while editing
  const [revising, setRevising] = useState(false);
  const { user } = useAuth();
  const canRevise = user?.role === 'admin';

  const effectiveDays = period === 'custom' ? Math.max(1, parseInt(customDays) || 30) : parseInt(period);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/credits/overview', { params: { days: effectiveDays, type: typeFilter } });
      setItems(res.data.items || []);
      setSummary(res.data.summary || null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load credits overview');
    } finally {
      setLoading(false);
    }
  }, [api, effectiveDays, typeFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openDetail = async (it) => {
    setDetail(it);
    setRevise(null);
    setDetailLoading(true);
    try {
      const res = await api.get(`/credits/overview/${it.wallet_id}/transactions`, { params: { days: effectiveDays * 4 } });
      setDetailTxns(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load transactions');
    } finally {
      setDetailLoading(false);
    }
  };

  const triggerRenewal = async () => {
    if (!isAdmin) return;
    setRenewing(true);
    try {
      const res = await api.post('/credits/trigger-renewal-now');
      toast.success(`Renewed ${res.data.renewed_count} wallet(s)`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Renewal failed');
    } finally {
      setRenewing(false);
    }
  };

  const startRevise = () => setRevise({
    monthly_allocation: String(detail.monthly_allocation ?? 0),
    balance: String(detail.balance ?? 0),
    auto_renew: !!detail.auto_renew,
    reason: '',
  });

  const submitRevise = async () => {
    const alloc = parseFloat(revise.monthly_allocation);
    const bal = parseFloat(revise.balance);
    if (Number.isNaN(alloc) || alloc < 0 || Number.isNaN(bal) || bal < 0) {
      toast.error('Enter valid non-negative numbers');
      return;
    }
    // Only send fields the admin touched, so a stale pre-filled balance can't overwrite a newer debit.
    const payload = { reason: revise.reason || null };
    if (alloc !== Number(detail.monthly_allocation || 0)) payload.monthly_allocation = alloc;
    if (bal !== Number(detail.balance || 0)) payload.balance = bal;
    if (revise.auto_renew !== !!detail.auto_renew) payload.auto_renew = revise.auto_renew;
    if (Object.keys(payload).length === 1) {
      toast.error('Nothing changed');
      return;
    }
    setRevising(true);
    try {
      const res = await api.post(`/credits/overview/${detail.wallet_id}/revise`, payload);
      toast.success(`Credits revised: ${res.data.changes.join(', ')}`);
      const updated = {
        ...detail,
        balance: res.data.balance,
        monthly_allocation: res.data.monthly_allocation,
        auto_renew: res.data.auto_renew,
      };
      fetchData();
      openDetail(updated);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to revise credits');
    } finally {
      setRevising(false);
    }
  };

  const filtered = items.filter((it) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (it.name || '').toLowerCase().includes(q) || (it.email || '').toLowerCase().includes(q);
  });

  const fmt = (v) => Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });

  return (
    <div className="space-y-6" data-testid="credits-overview-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-heading">Credits Overview</h2>
          <p className="text-sm text-muted-foreground">Live balance, allocation, usage and credit-purchase totals across {isAdmin ? 'all clients' : 'your assigned centres'}.</p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          {isAdmin && (
            <Button variant="outline" size="sm" onClick={triggerRenewal} disabled={renewing} data-testid="credits-renewal-btn">
              {renewing ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-2" /> : <RefreshCw className="w-3.5 h-3.5 mr-2" />}
              Run Auto-Renewal Now
            </Button>
          )}
        </div>
      </div>

      {/* Summary cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card><CardContent className="p-4">
            <p className="text-xs text-muted-foreground flex items-center gap-1"><Wallet className="w-3 h-3" /> Total Balance</p>
            <p className="text-2xl font-bold mt-1" data-testid="credits-summary-balance">{fmt(summary.total_balance)}</p>
            <p className="text-xs text-muted-foreground">across {summary.wallets} wallet(s)</p>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <p className="text-xs text-muted-foreground flex items-center gap-1"><TrendingDown className="w-3 h-3" /> Used in Period</p>
            <p className="text-2xl font-bold mt-1 text-orange-600" data-testid="credits-summary-used">{fmt(summary.total_used_in_period)}</p>
            <p className="text-xs text-muted-foreground">last {summary.period_days} days</p>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <p className="text-xs text-muted-foreground flex items-center gap-1"><IndianRupee className="w-3 h-3" /> Razorpay Purchases</p>
            <p className="text-2xl font-bold mt-1">₹{fmt(summary.total_inr_paid_for_credits)}</p>
            <p className="text-xs text-muted-foreground">total spent on credit packs</p>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <p className="text-xs text-muted-foreground flex items-center gap-1"><Coins className="w-3 h-3" /> Avg Balance / Wallet</p>
            <p className="text-2xl font-bold mt-1">{summary.wallets ? fmt(summary.total_balance / summary.wallets) : 0}</p>
          </CardContent></Card>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input className="pl-9" placeholder="Search by name or email…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="credits-search" />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-[160px]" data-testid="credits-type-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Wallets</SelectItem>
            <SelectItem value="orgs">Organizations</SelectItem>
            <SelectItem value="users">Individual Users</SelectItem>
          </SelectContent>
        </Select>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-[170px]" data-testid="credits-period-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            {PERIODS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
          </SelectContent>
        </Select>
        {period === 'custom' && (
          <Input type="number" min={1} max={3650} value={customDays} onChange={(e) => setCustomDays(e.target.value)} className="w-[120px]" placeholder="Days" data-testid="credits-custom-days" />
        )}
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : filtered.length === 0 ? (
        <Card><CardContent className="py-12 text-center">
          <Coins className="w-12 h-12 mx-auto text-muted-foreground/50 mb-3" />
          <p className="text-muted-foreground">No credit wallets found.</p>
        </CardContent></Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-left">
                  <tr>
                    <th className="px-4 py-3 font-medium">Client</th>
                    <th className="px-4 py-3 font-medium text-right">Monthly</th>
                    <th className="px-4 py-3 font-medium text-right">Top-up</th>
                    <th className="px-4 py-3 font-medium text-right">Total Allocated</th>
                    <th className="px-4 py-3 font-medium text-right">Used <span className="text-muted-foreground font-normal">({effectiveDays}d)</span></th>
                    <th className="px-4 py-3 font-medium text-right">₹ Razorpay</th>
                    <th className="px-4 py-3 font-medium">Last Activity</th>
                    <th className="px-4 py-3 font-medium text-right">Cycle</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((it) => (
                    <tr key={it.wallet_id} className="border-t border-border hover:bg-muted/30 cursor-pointer transition-colors" onClick={() => openDetail(it)} data-testid={`credits-row-${it.wallet_id}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {it.type === 'org' ? <Building2 className="w-4 h-4 text-primary" /> : <UserIcon className="w-4 h-4 text-muted-foreground" />}
                          <div className="min-w-0">
                            <div className="font-medium truncate">{it.name}</div>
                            <div className="text-xs text-muted-foreground truncate">{it.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right font-mono font-semibold text-blue-700">{fmt(it.balance)}</td>
                      <td className="px-4 py-3 text-right font-mono font-semibold text-amber-700">{fmt(it.topup_balance)}</td>
                      <td className="px-4 py-3 text-right font-mono">{fmt(it.total_allocated)}</td>
                      <td className="px-4 py-3 text-right font-mono text-orange-600">{fmt(it.used_in_period)}</td>
                      <td className="px-4 py-3 text-right font-mono">{it.total_inr_paid_for_credits > 0 ? `₹${fmt(it.total_inr_paid_for_credits)}` : '—'}</td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">{it.last_activity ? new Date(it.last_activity).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}</td>
                      <td className="px-4 py-3 text-right">
                        {it.auto_renew ? (
                          <Badge variant="outline" className="text-xs bg-green-500/10 text-green-700 border-green-500/30">{it.monthly_allocation}/{it.renewal_cycle_months}mo</Badge>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Transaction detail dialog */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-2xl w-[calc(100vw-2rem)] max-h-[85vh] overflow-y-auto overflow-x-hidden">
          <DialogHeader className="min-w-0 pr-6">
            <DialogTitle className="flex items-center gap-2 font-heading min-w-0">
              {detail?.type === 'org' ? <Building2 className="w-5 h-5 text-primary shrink-0" /> : <UserIcon className="w-5 h-5 text-primary shrink-0" />}
              <span className="truncate">{detail?.name}</span>
            </DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-4 min-w-0">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="bg-blue-500/10 rounded p-3 min-w-0">
                  <p className="text-xs text-blue-700">Monthly Balance</p>
                  <p className="text-xl font-bold font-mono truncate text-blue-700">{fmt(detail.balance)}</p>
                </div>
                <div className="bg-amber-500/10 rounded p-3 min-w-0">
                  <p className="text-xs text-amber-700">Top-up Balance</p>
                  <p className="text-xl font-bold font-mono truncate text-amber-700">{fmt(detail.topup_balance)}</p>
                </div>
                <div className="bg-muted/40 rounded p-3 min-w-0">
                  <p className="text-xs text-muted-foreground">Total Allocated</p>
                  <p className="text-xl font-bold font-mono truncate">{fmt(detail.total_allocated)}</p>
                </div>
                <div className="bg-muted/40 rounded p-3 min-w-0">
                  <p className="text-xs text-muted-foreground">Razorpay Spend</p>
                  <p className="text-xl font-bold font-mono truncate">₹{fmt(detail.total_inr_paid_for_credits)}</p>
                </div>
              </div>
              <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                <span className="text-muted-foreground">
                  Monthly allocation: <span className="font-mono font-semibold text-foreground">{fmt(detail.monthly_allocation)}</span>
                  {' · '}Auto-renew: <span className="font-semibold text-foreground">{detail.auto_renew ? 'On' : 'Off'}</span>
                </span>
                {canRevise && !revise && (
                  <Button variant="outline" size="sm" onClick={startRevise} data-testid="credits-revise-btn">
                    <Pencil className="w-3.5 h-3.5 mr-2" /> Revise Credits
                  </Button>
                )}
              </div>
              {canRevise && revise && (
                <div className="border rounded p-4 space-y-3 bg-muted/20" data-testid="credits-revise-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-muted-foreground">Monthly allocation (each renewal)</label>
                      <Input type="number" min={0} value={revise.monthly_allocation}
                        onChange={(e) => setRevise({ ...revise, monthly_allocation: e.target.value })}
                        data-testid="credits-revise-allocation" />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground">Current monthly balance</label>
                      <Input type="number" min={0} value={revise.balance}
                        onChange={(e) => setRevise({ ...revise, balance: e.target.value })}
                        data-testid="credits-revise-balance" />
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch checked={revise.auto_renew} onCheckedChange={(v) => setRevise({ ...revise, auto_renew: v })} data-testid="credits-revise-autorenew" />
                    <span className="text-sm">Auto-renew monthly</span>
                  </div>
                  <Input placeholder="Reason (optional, shown in transaction history)" value={revise.reason}
                    onChange={(e) => setRevise({ ...revise, reason: e.target.value })} data-testid="credits-revise-reason" />
                  <p className="text-xs text-muted-foreground">
                    Values are set, not added. The new allocation applies from the next renewal; change the current balance to apply it now. Top-up balance is not affected.
                  </p>
                  <div className="flex justify-end gap-2">
                    <Button variant="ghost" size="sm" onClick={() => setRevise(null)} disabled={revising}>Cancel</Button>
                    <Button size="sm" onClick={submitRevise} disabled={revising} data-testid="credits-revise-save">
                      {revising && <Loader2 className="w-3.5 h-3.5 animate-spin mr-2" />} Save
                    </Button>
                  </div>
                </div>
              )}
              <div>
                <h3 className="font-medium mb-2">Transaction History</h3>
                {detailLoading ? (
                  <div className="flex items-center justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>
                ) : detailTxns.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4 text-center">No transactions in this window.</p>
                ) : (
                  <div className="border rounded divide-y max-h-[40vh] overflow-y-auto">
                    {detailTxns.map((t) => (
                      <div key={t.id} className="p-3 flex items-start justify-between gap-3" data-testid={`credit-txn-${t.id}`}>
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <Badge variant="outline" className="text-xs">{t.transaction_type}</Badge>
                            <span className="text-xs text-muted-foreground">{new Date(t.created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}</span>
                          </div>
                          <p className="text-sm mt-1 break-words">{t.description}</p>
                        </div>
                        <span className={`shrink-0 font-mono font-semibold ${t.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {t.amount >= 0 ? '+' : ''}{fmt(t.amount)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CreditsOverviewTab;
